/**
 * Wave F (decision 0062): usage rows as every reader sees them, rollups, D1 ledger rows and the facts a run's cost chip
 * is made of. Pure: works on runs read from the Project DO's SQL table as well as on `ProjectState.runs`.
 *
 * Two numbers per row: `listMicroUsd` (≈ at API list price, whenever the model is priced, also for subscription use)
 * and `costMicroUsd` (marginal money actually charged; null for included use and for unknown). Who paid (`fundedBy`)
 * is never who asked (`askedBy`).
 */
import type {
  Actor,
  BillingClass,
  ComputeUsage,
  FundedBy,
  PlanRef,
  Principal,
  Run,
  RunKind,
  Usage,
  UsageTotalsSchema,
} from "@minga/protocol"
import { computeListMicroUsd, modelKey, pricedRow } from "./pricing"

// #region normalization
/**
 * A row as every reader sees it: `inputTokens` UNCACHED, cache writes and billing class present, prices filled. Never
 * stored. `pricedLater`: the row had no price when it was stored (a run before wave F, or keys an older cloud dropped)
 * and was priced now from the current table (the UI says "priced later").
 */
export type UsageV2 = Usage & {
  readonly billingClass: BillingClass
  readonly cacheWriteTokens: number
  readonly pricedLater: boolean
}

/**
 * The billing class of a stored row. Absent (rows before wave F): a `metered` row without a profile is the cloud mind
 * on an API key; every other row is `unknown` (an old runner's `subscription_included` was always written, so it is
 * not evidence).
 */
export const billingClassOf = (row: Usage): BillingClass =>
  row.billingClass ?? (row.billing === "metered" && row.profileId === null ? "api_key" : "unknown")

/**
 * Uncached input of a stored row, from keys every cloud version keeps (`provider`, `profileId`), so a row is never
 * normalized twice across version skew (Review note R1). w = cacheWriteTokens ?? 0:
 * - provider "openai" (Codex runner rows, OpenAI mind rows): max(0, input − cached − w). Codex reports cached input
 *   (and, unverified U1, cache writes) inside `inputTokens`.
 * - provider "anthropic" without a profile (AI SDK mind rows; the AI SDK total includes cache reads and writes):
 *   max(0, input − cached − w). Old rows have w = 0, so their writes stay inside input, priced at 1× input instead of
 *   1.25× (≈, slightly LOW).
 * - provider "anthropic" with a profile (Claude runner rows): already uncached.
 */
export const uncachedInputOf = (row: Usage): number => {
  const w = row.cacheWriteTokens ?? 0
  return row.provider === "openai" || row.profileId === null
    ? Math.max(0, row.inputTokens - row.cachedInputTokens - w)
    : row.inputTokens
}

/** Normalized tokens and class, without touching prices. */
const normalized = (row: Usage): UsageV2 => ({
  ...row,
  inputTokens: uncachedInputOf(row),
  billingClass: billingClassOf(row),
  cacheWriteTokens: row.cacheWriteTokens ?? 0,
  pricedLater: false,
})

/**
 * Prices one STORED row (decide does this at RecordRunEnded): fills `billingClass`, legacy `billing`, `listMicroUsd`,
 * `costMicroUsd`, `costSource` and `priceTableVersion` (only where absent) and keeps the stored token fields in the
 * source's own meaning. See `pricedRow` for the rules.
 */
export const priceUsage = (row: Usage): Usage => pricedRow(row, normalized(row))

/**
 * Read-time normalization, applied to every row on every read (decision 0036 style; nothing stored is rewritten):
 * uncached input, cache writes, billing class, and prices for rows stored without them (current table, marked
 * `pricedLater`).
 */
export const usageRowV2 = (row: Usage): UsageV2 => {
  const n = normalized(row)
  if (row.listMicroUsd !== undefined) return n
  // A runner row stored before wave F (no billingClass, a profile): main loop only and no cache writes (Claude), so
  // the table price is a floor: "≈", as decide marks such rows from an old runner today.
  const legacyRunner =
    row.billingClass === undefined && row.profileId !== null && row.costSource === undefined
  const p = pricedRow(legacyRunner ? { ...row, costSource: "estimate" } : row, n)
  return {
    ...p,
    inputTokens: n.inputTokens,
    billingClass: n.billingClass,
    cacheWriteTokens: n.cacheWriteTokens,
    pricedLater: true,
  }
}

/** Uncached input + cache writes + output: the fresh work. What a chip's "tok" means. */
export const freshTokens = (row: UsageV2): number => row.inputTokens + row.cacheWriteTokens + row.outputTokens
/** freshTokens + cache reads. Details only (cache reads dominate agent runs). */
export const totalTokens = (row: UsageV2): number => freshTokens(row) + row.cachedInputTokens
// #endregion normalization

// #region dims
/** Rows of these classes cost money; with a null cost they count as "charged, amount unknown". */
const CHARGES: ReadonlySet<BillingClass> = new Set<BillingClass>([
  "api_key",
  "subscription_extra",
  "vendor_credits",
  "compute",
])
const BILLING_ORDER: ReadonlyArray<BillingClass> = [
  "subscription",
  "subscription_extra",
  "api_key",
  "vendor_credits",
  "compute",
  "unknown",
]

/** The UTC day of a time, "YYYY-MM-DD". */
export const utcDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10)

/** "user:<id>" | "agent:<id>" | "unknown". */
export const principalKey = (p: Principal | null | undefined): string =>
  !p ? "unknown" : p._tag === "user" ? `user:${p.userId}` : `agent:${p.agentId}`

/** "user:<id>" (a plan's seat holder or a key's owner) | "workspace" | "platform" | "unknown". */
export const paidByKey = (f: FundedBy | null | undefined): string => {
  if (!f) return "unknown"
  if (f.kind === "workspace" || f.kind === "platform") return f.kind
  if ((f.kind === "plan" || f.kind === "key") && f.ownerId) return `user:${f.ownerId}`
  return "unknown"
}

/** "anthropic:max", or "" without a plan. */
export const planKey = (p: PlanRef | null | undefined): string => (p ? `${p.provider}:${p.tier}` : "")

/** The dimensions of one run. */
export interface UsageDims {
  readonly runId: string
  readonly kind: RunKind
  /** UTC day of the end (live runs: of the start, else of the request). */
  readonly day: string
  readonly itemId: string | null
  readonly topItemId: string | null
  readonly areaId: string | null
  /** turn: the agent; worker/validator: the supervising agent ("her workers"); publish: the item's owner agent. */
  readonly agentId: string | null
  /** true for the agent's own turns, false for her workers', validators' and publish runs. */
  readonly own: boolean
  readonly askedBy: string
  /** The run's default payer; a row's own `fundedBy` override wins for that row. */
  readonly paidBy: string
  readonly fundingKind: FundedBy["kind"]
  readonly plan: PlanRef | null
}

export const usageDims = (run: Run): UsageDims => ({
  runId: run.id,
  kind: run.kind,
  day: utcDay(run.endedAt ?? run.startedAt ?? run.requestedAt),
  itemId: run.itemId,
  topItemId: run.attribution.topItemId,
  areaId: run.attribution.areaId,
  agentId:
    run.kind === "worker" || run.kind === "validator"
      ? (run.worker?.supervisorId ?? run.agentId)
      : run.agentId,
  own: run.kind === "turn",
  askedBy: principalKey(run.askedBy),
  paidBy: paidByKey(run.fundedBy),
  fundingKind: run.fundedBy?.kind ?? "unknown",
  plan: run.fundedBy?.plan ?? null,
})

/**
 * The run's primary row, where it counts in `runs` (once, never once per row): the token row of the session's main
 * model (the one carrying wallMs/turns/toolCalls), else the first token row, else the first compute row. A run with no
 * rows counts in no group.
 */
export const primaryRowOf = (run: Run): { kind: "usage" | "compute"; index: number } | null => {
  const main = run.usage.findIndex((u) => u.wallMs > 0 || u.turns > 0 || u.toolCalls > 0)
  if (main >= 0) return { kind: "usage", index: main }
  if (run.usage.length > 0) return { kind: "usage", index: 0 }
  if ((run.compute?.length ?? 0) > 0) return { kind: "compute", index: 0 }
  return null
}
// #endregion dims

// #region totals
export type UsageTotals = UsageTotalsSchema

interface Acc {
  runs: number
  inputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  outputTokens: number
  listMicroUsd: number
  unpricedRows: number
  chargedMicroUsd: number
  chargedUnknownRows: number
  computeMicroUsd: number
  byBilling: Map<BillingClass, { listMicroUsd: number; chargedMicroUsd: number; runs: number }>
}

const emptyAcc = (): Acc => ({
  runs: 0,
  inputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  outputTokens: 0,
  listMicroUsd: 0,
  unpricedRows: 0,
  chargedMicroUsd: 0,
  chargedUnknownRows: 0,
  computeMicroUsd: 0,
  byBilling: new Map(),
})

/** One row's contribution, in ledger units (also what `ledgerRowsOf` stores). */
interface Contribution {
  readonly billing: BillingClass
  readonly runs: number
  readonly inputTokens: number
  readonly cacheReadTokens: number
  readonly cacheWriteTokens: number
  readonly outputTokens: number
  readonly listMicroUsd: number
  readonly unpricedRows: number
  readonly costMicroUsd: number
  readonly costUnknownRows: number
  readonly computeMicroUsd: number
}

const add = (acc: Acc, c: Contribution): void => {
  acc.runs += c.runs
  acc.inputTokens += c.inputTokens
  acc.cacheReadTokens += c.cacheReadTokens
  acc.cacheWriteTokens += c.cacheWriteTokens
  acc.outputTokens += c.outputTokens
  acc.listMicroUsd += c.listMicroUsd
  acc.unpricedRows += c.unpricedRows
  acc.chargedMicroUsd += c.costMicroUsd
  acc.chargedUnknownRows += c.costUnknownRows
  acc.computeMicroUsd += c.computeMicroUsd
  const b = acc.byBilling.get(c.billing) ?? { listMicroUsd: 0, chargedMicroUsd: 0, runs: 0 }
  acc.byBilling.set(c.billing, {
    listMicroUsd: b.listMicroUsd + c.listMicroUsd,
    chargedMicroUsd: b.chargedMicroUsd + c.costMicroUsd,
    runs: b.runs + c.runs,
  })
}

const freeze = (acc: Acc): UsageTotals => ({
  runs: acc.runs,
  inputTokens: acc.inputTokens,
  cacheReadTokens: acc.cacheReadTokens,
  cacheWriteTokens: acc.cacheWriteTokens,
  outputTokens: acc.outputTokens,
  listMicroUsd: acc.listMicroUsd,
  unpricedRows: acc.unpricedRows,
  chargedMicroUsd: acc.chargedMicroUsd,
  chargedUnknownRows: acc.chargedUnknownRows,
  computeMicroUsd: acc.computeMicroUsd,
  byBilling: BILLING_ORDER.flatMap((billing) => {
    const b = acc.byBilling.get(billing)
    return b ? [{ billing, ...b }] : []
  }),
})

export const emptyTotals = (): UsageTotals => freeze(emptyAcc())

const tokenContribution = (row: UsageV2, primary: boolean): Contribution => ({
  billing: row.billingClass,
  runs: primary ? 1 : 0,
  inputTokens: row.inputTokens,
  cacheReadTokens: row.cachedInputTokens,
  cacheWriteTokens: row.cacheWriteTokens,
  outputTokens: row.outputTokens,
  listMicroUsd: row.listMicroUsd ?? 0,
  unpricedRows: row.listMicroUsd == null ? 1 : 0,
  costMicroUsd: row.costMicroUsd ?? 0,
  costUnknownRows: row.costMicroUsd === null && CHARGES.has(row.billingClass) ? 1 : 0,
  computeMicroUsd: 0,
})

const computeContribution = (c: ComputeUsage, primary: boolean): Contribution => ({
  billing: "compute",
  runs: primary ? 1 : 0,
  inputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  outputTokens: 0,
  listMicroUsd: c.listMicroUsd,
  unpricedRows: 0,
  costMicroUsd: c.costMicroUsd ?? 0,
  // A platform-paid sandbox (decision 0107) charges nobody in the workspace: its list price is shown, no unknown charge.
  costUnknownRows: c.costMicroUsd === null && c.fundedBy?.kind !== "platform" ? 1 : 0,
  computeMicroUsd: c.listMicroUsd,
})

/** Every row of a run with its contribution and its own payer (a row's `fundedBy` override wins). */
interface RunRow {
  readonly contribution: Contribution
  readonly model: string
  readonly provider: string
  readonly unit: string
  readonly quantity: number
  readonly fundedBy: FundedBy | null
}

const rowsOfRun = (run: Run): ReadonlyArray<RunRow> => {
  const primary = primaryRowOf(run)
  const out: RunRow[] = []
  run.usage.forEach((u, i) => {
    const v2 = usageRowV2(u)
    out.push({
      contribution: tokenContribution(v2, primary?.kind === "usage" && primary.index === i),
      model: modelKey(u.provider, u.model),
      provider: u.provider,
      unit: u.vendorUnits?.unit ?? "",
      quantity: u.vendorUnits?.amount ?? 0,
      fundedBy: u.fundedBy ?? run.fundedBy ?? null,
    })
  })
  ;(run.compute ?? []).forEach((c, i) => {
    out.push({
      contribution: computeContribution(c, primary?.kind === "compute" && primary.index === i),
      model: `compute:${c.instanceType}`,
      provider: c.provider,
      unit: "compute_s",
      quantity: c.runningSeconds,
      fundedBy: c.fundedBy ?? run.fundedBy ?? null,
    })
  })
  return out
}

/** All rows of one run. */
export const runTotals = (run: Run): UsageTotals => {
  const acc = emptyAcc()
  for (const r of rowsOfRun(run)) add(acc, r.contribution)
  return freeze(acc)
}

export type UsageBy =
  | "run"
  | "item"
  | "top_item"
  | "agent"
  | "asked_by"
  | "paid_by"
  | "model"
  | "billing"
  | "day"
  | "project"

export interface UsageFilter {
  /** UTC days, inclusive. */
  readonly from?: string
  readonly to?: string
  readonly billing?: ReadonlyArray<BillingClass>
  /** The run's item, or (for a top-level item) any run under it. */
  readonly itemId?: string
  /**
   * With `itemId`: the item and every item under it (`itemsUnder`), so a mid-level item counts its sub-items' runs too
   * (one scope for every level; the item page labels it "incl. sub-items").
   */
  readonly itemIds?: ReadonlySet<string>
  /** `UsageDims.agentId`: her own turns and her workers. */
  readonly agentId?: string
}

export interface UsageRollupGroup {
  readonly key: string
  readonly totals: UsageTotals
  /** `by: "agent"` only: her own turns, and her workers' / validators' / publish runs. */
  readonly own?: UsageTotals
  readonly workers?: UsageTotals
}

const NONE = "none"

const keyOf = (by: UsageBy, d: UsageDims, r: RunRow): string => {
  switch (by) {
    case "run":
      return d.runId
    case "item":
      return d.itemId ?? NONE
    case "top_item":
      return d.topItemId ?? NONE
    case "agent":
      return d.agentId ?? NONE
    case "asked_by":
      return d.askedBy
    case "paid_by":
      return paidByKey(r.fundedBy)
    case "model":
      return r.model
    case "billing":
      return r.contribution.billing
    case "day":
      return d.day
    case "project":
      return "project"
  }
}

const runPasses = (d: UsageDims, f: UsageFilter | undefined): boolean =>
  !f ||
  ((f.from === undefined || d.day >= f.from) &&
    (f.to === undefined || d.day <= f.to) &&
    (f.itemId === undefined ||
      d.itemId === f.itemId ||
      d.topItemId === f.itemId ||
      (d.itemId !== null && f.itemIds?.has(d.itemId) === true)) &&
    (f.agentId === undefined || d.agentId === f.agentId))

/**
 * Totals grouped by one dimension, sorted by `listMicroUsd` descending (then key). A run counts once in `runs`, on its
 * primary row: under `by: "model"`, `runs` means "runs whose main model was X" while tokens and money split per model.
 * The billing filter applies per row.
 */
export const rollup = (
  runs: Iterable<Run>,
  by: UsageBy,
  f?: UsageFilter,
): ReadonlyArray<UsageRollupGroup> => {
  const groups = new Map<string, { all: Acc; own: Acc; workers: Acc }>()
  for (const run of runs) {
    const d = usageDims(run)
    if (!runPasses(d, f)) continue
    for (const r of rowsOfRun(run)) {
      if (f?.billing && !f.billing.includes(r.contribution.billing)) continue
      const key = keyOf(by, d, r)
      let g = groups.get(key)
      if (!g) {
        g = { all: emptyAcc(), own: emptyAcc(), workers: emptyAcc() }
        groups.set(key, g)
      }
      add(g.all, r.contribution)
      add(d.own ? g.own : g.workers, r.contribution)
    }
  }
  return [...groups.entries()]
    .map(([key, g]) => ({
      key,
      totals: freeze(g.all),
      ...(by === "agent" ? { own: freeze(g.own), workers: freeze(g.workers) } : {}),
    }))
    .sort(
      (a, b) => b.totals.listMicroUsd - a.totals.listMicroUsd || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
    )
}

/** One total over every run that passes the filter. */
export const totalOf = (runs: Iterable<Run>, f?: UsageFilter): UsageTotals =>
  rollup(runs, "project", f)[0]?.totals ?? emptyTotals()

/**
 * Decision 0167 §2: what a person's plans paid in this project, per connection and account it ran on and per asker.
 * Only rows a plan paid (`fundedBy.kind === "plan"` with an owner); `accountKey` null on rows from before 0167 (the
 * caller maps those through its directory). `lastAt`/`extraUsage`: the latest run of the group and whether it ran on
 * extra usage.
 */
export interface SubscriptionUsageRow {
  readonly ownerId: string
  readonly runnerId: string | null
  readonly profileId: string | null
  readonly accountKey: string | null
  /** `principalKey` of who asked ("user:<id>", "agent:<id>", "unknown"). */
  readonly askedBy: string
  readonly runs: number
  readonly listMicroUsd: number
  readonly lastAt: number
  readonly extraUsage: boolean
}

export const subscriptionUsage = (
  runs: Iterable<Run>,
  f?: Pick<UsageFilter, "from" | "to">,
): ReadonlyArray<SubscriptionUsageRow> => {
  const groups = new Map<string, { -readonly [K in keyof SubscriptionUsageRow]: SubscriptionUsageRow[K] }>()
  for (const run of runs) {
    const d = usageDims(run)
    if (!runPasses(d, f)) continue
    const at = run.endedAt ?? run.startedAt ?? run.requestedAt
    for (const r of rowsOfRun(run)) {
      const funded = r.fundedBy
      if (funded?.kind !== "plan" || !funded.ownerId) continue
      const row = {
        ownerId: String(funded.ownerId),
        runnerId: funded.runnerId ? String(funded.runnerId) : null,
        profileId: funded.profileId ? String(funded.profileId) : null,
        accountKey: funded.accountKey ?? null,
        askedBy: d.askedBy,
      }
      const key = [row.ownerId, row.runnerId, row.profileId, row.accountKey, row.askedBy].join("\n")
      let g = groups.get(key)
      if (!g) {
        g = { ...row, runs: 0, listMicroUsd: 0, lastAt: at, extraUsage: funded.extraUsage === true }
        groups.set(key, g)
      }
      g.runs += r.contribution.runs
      g.listMicroUsd += r.contribution.listMicroUsd
      if (at > g.lastAt) {
        g.lastAt = at
        g.extraUsage = funded.extraUsage === true
      }
    }
  }
  return [...groups.values()]
}
// #endregion totals

// #region ledger
/** One D1 `usage_daily` delta (migration 0006): the key columns plus sums. */
export interface LedgerRow {
  readonly workspaceId: string
  readonly day: string
  readonly projectId: string
  readonly askedBy: string
  readonly paidBy: string
  readonly fundedKind: FundedBy["kind"]
  /** planKey: "anthropic:max", '' when none. */
  readonly plan: string
  /** '' when none. */
  readonly agentId: string
  readonly runKind: RunKind
  readonly billing: BillingClass
  readonly provider: string
  /** Canonical id, or the raw id when unpriced; "compute:<instance type>" for compute. */
  readonly model: string
  /** '' for token rows; "compute_s" for compute; a vendor unit ("acu" …). */
  readonly unit: string
  readonly runs: number
  readonly inputTokens: number
  readonly cacheReadTokens: number
  readonly cacheWriteTokens: number
  readonly outputTokens: number
  readonly listMicroUsd: number
  readonly unpricedRows: number
  readonly costMicroUsd: number
  readonly costUnknownRows: number
  /** In `unit`: compute seconds, vendor units; 0 for token rows. */
  readonly quantity: number
}

/** The primary-key columns of a ledger row, joined (rows of one run with the same key are merged). */
export const ledgerKey = (r: LedgerRow): string =>
  [
    r.workspaceId,
    r.day,
    r.projectId,
    r.askedBy,
    r.paidBy,
    r.fundedKind,
    r.plan,
    r.agentId,
    r.runKind,
    r.billing,
    r.provider,
    r.model,
    r.unit,
  ].join("\u0000")

/**
 * The D1 ledger deltas of one ended run: one per (run, row), merged where the key repeats. `runs` is 1 only on the
 * primary row. paid_by / funded_kind / plan come from the row's own `fundedBy` override, else `Run.fundedBy`.
 * `workspaceId` and `projectId` must come from the Project DO's own identity, never from a request.
 */
export const ledgerRowsOf = (run: Run, workspaceId: string, projectId: string): ReadonlyArray<LedgerRow> => {
  const d = usageDims(run)
  const out = new Map<string, LedgerRow>()
  for (const r of rowsOfRun(run)) {
    const c = r.contribution
    const row: LedgerRow = {
      workspaceId,
      day: d.day,
      projectId,
      askedBy: d.askedBy,
      paidBy: paidByKey(r.fundedBy),
      fundedKind: r.fundedBy?.kind ?? "unknown",
      plan: planKey(r.fundedBy?.plan),
      agentId: d.agentId ?? "",
      runKind: run.kind,
      billing: c.billing,
      provider: r.provider,
      model: r.model,
      unit: r.unit,
      runs: c.runs,
      inputTokens: c.inputTokens,
      cacheReadTokens: c.cacheReadTokens,
      cacheWriteTokens: c.cacheWriteTokens,
      outputTokens: c.outputTokens,
      listMicroUsd: c.listMicroUsd,
      unpricedRows: c.unpricedRows,
      costMicroUsd: c.costMicroUsd,
      costUnknownRows: c.costUnknownRows,
      quantity: r.quantity,
    }
    const k = ledgerKey(row)
    const prev = out.get(k)
    out.set(
      k,
      prev
        ? {
            ...prev,
            runs: prev.runs + row.runs,
            inputTokens: prev.inputTokens + row.inputTokens,
            cacheReadTokens: prev.cacheReadTokens + row.cacheReadTokens,
            cacheWriteTokens: prev.cacheWriteTokens + row.cacheWriteTokens,
            outputTokens: prev.outputTokens + row.outputTokens,
            listMicroUsd: prev.listMicroUsd + row.listMicroUsd,
            unpricedRows: prev.unpricedRows + row.unpricedRows,
            costMicroUsd: prev.costMicroUsd + row.costMicroUsd,
            costUnknownRows: prev.costUnknownRows + row.costUnknownRows,
            quantity: prev.quantity + row.quantity,
          }
        : row,
    )
  }
  return [...out.values()]
}

/** Sums ledger rows into totals (the workspace and personal reports read D1 rows through this). */
export const ledgerTotals = (rows: Iterable<LedgerRow>): UsageTotals => {
  const acc = emptyAcc()
  for (const r of rows)
    add(acc, {
      billing: r.billing,
      runs: r.runs,
      inputTokens: r.inputTokens,
      cacheReadTokens: r.cacheReadTokens,
      cacheWriteTokens: r.cacheWriteTokens,
      outputTokens: r.outputTokens,
      listMicroUsd: r.listMicroUsd,
      unpricedRows: r.unpricedRows,
      costMicroUsd: r.costMicroUsd,
      costUnknownRows: r.costUnknownRows,
      computeMicroUsd: r.billing === "compute" ? r.listMicroUsd : 0,
    })
  return freeze(acc)
}
// #endregion ledger

// #region chip
export type RunCostMode = "included" | "extra" | "charged" | "unknown" | "unpriced" | "none"

export interface RunCostFacts {
  /** Σ freshTokens. */
  readonly tokens: number
  readonly cacheReadTokens: number
  /** Model use only (token rows); null when no row is priced. Sandbox compute is apart, in `sandbox`. */
  readonly listMicroUsd: number | null
  /** null when nothing was charged that Minga knows of (token rows; a sandbox's charge is in `sandbox`). */
  readonly chargedMicroUsd: number | null
  readonly mode: RunCostMode
  /** Some figure is a rougher guess (step sums, a reroute split, extra usage): the UI says "≈". */
  readonly estimate: boolean
  /** Some row was priced at read time from the current table. */
  readonly pricedLater: boolean
  readonly fundedBy: FundedBy | null
  readonly askedBy: Principal | null
  readonly rows: ReadonlyArray<UsageV2>
  /** The run's sandbox compute (Platform Runner task sandboxes, decision 0107); null without compute rows. */
  readonly sandbox: SandboxCostFacts | null
}

export interface SandboxCostFacts {
  /** At list price, an upper bound (CPU counted at 100%). */
  readonly listMicroUsd: number
  /** null: not knowable per run (an account's included allowance), or nobody charged it. */
  readonly chargedMicroUsd: number | null
  readonly runningSeconds: number
  /** "standard-1", or several joined ("standard-1, standard-2") when a relaunch changed the size. */
  readonly instanceType: string
  readonly fundedBy: FundedBy | null
}

/**
 * Structured facts for one run's chip; the web turns them into words. Mode (token rows): `charged` when a key (or
 * vendor) row charged money, else `extra` with any extra-usage row, else `unknown` with any row of unknown billing,
 * else `included`; `unpriced` when nothing is priced or charged; `none` without tokens. Never "$0" for included use:
 * an included run has `chargedMicroUsd: null`. Sandbox compute never changes the mode; it is its own line (0107).
 */
export const runCostFacts = (run: Run): RunCostFacts => {
  const rows = run.usage.map(usageRowV2)
  const compute = run.compute ?? []
  const tokens = rows.reduce((n, r) => n + freshTokens(r), 0)
  const cacheReadTokens = rows.reduce((n, r) => n + r.cachedInputTokens, 0)
  const priced = rows.filter((r) => r.listMicroUsd != null)
  const listMicroUsd = priced.length === 0 ? null : priced.reduce((n, r) => n + (r.listMicroUsd ?? 0), 0)
  const charges = rows.filter((r) => r.costMicroUsd !== null).map((r) => r.costMicroUsd ?? 0)
  const chargedMicroUsd = charges.length === 0 ? null : charges.reduce((a, b) => a + b, 0)
  const classes = new Set(rows.map((r) => r.billingClass))
  const charged = rows.some((r) => r.costMicroUsd !== null && r.billingClass !== "subscription_extra")
  const mode: RunCostMode =
    tokens === 0
      ? "none"
      : listMicroUsd === null && chargedMicroUsd === null
        ? "unpriced"
        : charged || classes.has("api_key")
          ? "charged"
          : classes.has("subscription_extra")
            ? "extra"
            : classes.has("unknown") || classes.has("vendor_credits")
              ? "unknown"
              : "included"
  const computeCharges = compute.filter((c) => c.costMicroUsd !== null).map((c) => c.costMicroUsd ?? 0)
  const sandbox: SandboxCostFacts | null =
    compute.length === 0
      ? null
      : {
          listMicroUsd: compute.reduce((n, c) => n + c.listMicroUsd, 0),
          chargedMicroUsd: computeCharges.length === 0 ? null : computeCharges.reduce((a, b) => a + b, 0),
          runningSeconds: compute.reduce((n, c) => n + c.runningSeconds, 0),
          instanceType: [...new Set(compute.map((c) => c.instanceType))].join(", "),
          fundedBy: compute[0]?.fundedBy ?? run.fundedBy ?? null,
        }
  return {
    tokens,
    cacheReadTokens,
    listMicroUsd,
    chargedMicroUsd,
    mode,
    estimate: rows.some((r) => r.costSource === "estimate"),
    pricedLater: rows.some((r) => r.pricedLater),
    fundedBy: run.fundedBy ?? null,
    askedBy: run.askedBy ?? null,
    rows,
    sandbox,
  }
}
// #endregion chip

/** An item and every item under it (by `parentId`), for an item's usage scope. */
export const itemsUnder = (
  items: ReadonlyArray<{ readonly id: string; readonly parentId: string | null }>,
  itemId: string,
): ReadonlySet<string> => {
  const children = new Map<string, string[]>()
  for (const it of items)
    if (it.parentId !== null) children.set(it.parentId, [...(children.get(it.parentId) ?? []), it.id])
  const out = new Set<string>([itemId])
  const queue = [itemId]
  for (let id = queue.pop(); id !== undefined; id = queue.pop())
    for (const c of children.get(id) ?? [])
      if (!out.has(c)) {
        out.add(c)
        queue.push(c)
      }
  return out
}

/** The runs a scope of `itemsUnder` counts (as `rollup`'s filter does): attempts and reviews next to the money. */
export const runsUnderItem = <
  R extends { readonly itemId: string | null; readonly attribution: { readonly topItemId: string | null } },
>(
  runs: ReadonlyArray<R>,
  itemId: string,
  under: ReadonlySet<string>,
): ReadonlyArray<R> =>
  runs.filter((r) => r.attribution.topItemId === itemId || (r.itemId !== null && under.has(r.itemId)))

// #region plans
const ANTHROPIC_PLANS: Readonly<Record<string, string>> = {
  pro: "Claude Pro",
  max: "Claude Max",
  team: "Claude Team",
  enterprise: "Claude Enterprise",
}
const OPENAI_PLANS: Readonly<Record<string, string>> = {
  free: "ChatGPT Free",
  go: "ChatGPT Go",
  plus: "ChatGPT Plus",
  pro: "ChatGPT Pro",
  prolite: "ChatGPT Pro Lite",
  promax: "ChatGPT Pro Max",
  team: "ChatGPT Team",
  business: "ChatGPT Business",
  enterprise: "ChatGPT Enterprise",
  ent26: "ChatGPT Enterprise",
  edu: "ChatGPT Edu",
  edu_plus: "ChatGPT Edu",
  edu_pro: "ChatGPT Edu",
}
/** Decision 0109: an Antigravity sign-in's Google AI plan; other Google tiers read "Google <Tier>". */
const GOOGLE_PLANS: Readonly<Record<string, string>> = {
  free: "Google AI Free",
  pro: "Google AI Pro",
  ultra: "Google AI Ultra",
}
const PROVIDER_NAME: Readonly<Record<string, string>> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
}

const titleCase = (s: string): string =>
  s
    .split(/[_\s-]+/)
    .filter((w) => w !== "")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")

/**
 * "Claude Max", "ChatGPT Pro", "ChatGPT Business" … for every Claude `subscriptionType` and every installed Codex
 * `PlanType`. Anything else, and "unknown": "<Provider> <Tier>" with the raw tier.
 */
export const planLabel = (p: PlanRef): string => {
  const tier = p.tier.trim().toLowerCase()
  if (p.provider === "anthropic" && ANTHROPIC_PLANS[tier]) return ANTHROPIC_PLANS[tier]
  if (p.provider === "google" && GOOGLE_PLANS[tier]) return GOOGLE_PLANS[tier]
  if (p.provider === "openai") {
    if (OPENAI_PLANS[tier]) return OPENAI_PLANS[tier]
    if (tier.startsWith("self_serve_business_")) return "ChatGPT Business"
    if (tier.startsWith("enterprise_cbp_")) return "ChatGPT Enterprise"
  }
  return `${PROVIDER_NAME[p.provider] ?? titleCase(p.provider)} ${titleCase(p.tier) || "Unknown"}`
}

/**
 * Who pays the seat: "personal" (Claude pro/max; ChatGPT free/go/plus/pro/prolite/promax), "org" (Claude
 * team/enterprise; ChatGPT team, business, self_serve_business_*, enterprise, ent26, enterprise_cbp_*, edu*), else
 * "unknown". An org seat is the organisation's money, not the person's. Derived, never stored.
 */
export const planSeat = (p: PlanRef): "personal" | "org" | "unknown" => {
  const tier = p.tier.trim().toLowerCase()
  if (p.provider === "anthropic") {
    if (tier === "pro" || tier === "max") return "personal"
    if (tier === "team" || tier === "enterprise") return "org"
    return "unknown"
  }
  // a Google tier never falls through to the ChatGPT lists below
  if (p.provider === "google") return GOOGLE_PLANS[tier] ? "personal" : "unknown"
  if (["free", "go", "plus", "pro", "prolite", "promax"].includes(tier)) return "personal"
  if (
    tier === "team" ||
    tier === "business" ||
    tier === "enterprise" ||
    tier === "ent26" ||
    tier.startsWith("self_serve_business_") ||
    tier.startsWith("enterprise_cbp_") ||
    tier === "edu" ||
    tier.startsWith("edu_")
  )
    return "org"
  return "unknown"
}
// #endregion plans

// #region decide
/**
 * A runner's row as decide accepts it: runners never price (wave F). Drops `listMicroUsd`, `priceTableVersion`, a
 * non-null `costMicroUsd` and any `costSource` but "estimate"; fills an absent `billingClass` per `billingClassOf`.
 * Drops a row's `fundedBy`: runners send no row overrides in wave F, and a payer's owner never comes from a runner
 * (decision 0062; `recordedUsage` logs the drop). `reported` (the CLI's own figure) stays.
 *
 * A row without `billingClass` comes from a runner before wave F: Claude's `result.usage` (main loop only, no cache
 * writes, no subagents) or Codex's turn total. Its table price is a floor, not the run's price: `estimate` ("≈").
 */
export const sanitizeRunnerUsage = (row: Usage): Usage => {
  const { listMicroUsd: _list, priceTableVersion: _version, costSource, fundedBy: _fundedBy, ...rest } = row
  const legacy = row.billingClass === undefined
  return {
    ...rest,
    billingClass: billingClassOf(row),
    costMicroUsd: null,
    ...(costSource === "estimate" || legacy ? { costSource: "estimate" as const } : {}),
  }
}

/**
 * Why a `fundedBy` (run default or a row's override) is not acceptable from this actor, or null when it is. Runner:
 * kind plan | key | unknown with its own runnerId. Agent (platform turns): workspace | platform with no owner. Anyone
 * else names no payer. Decide drops an unacceptable value (an end is never refused over bookkeeping); the cloud logs
 * the reason.
 */
export const fundedByProblem = (actor: Actor, f: FundedBy): string | null => {
  switch (actor._tag) {
    case "runner":
      if (f.runnerId !== actor.runnerId) return "a runner names only itself as the runner that paid"
      if (f.kind !== "plan" && f.kind !== "key" && f.kind !== "unknown")
        return `a runner run is paid by a plan or a key, not ${f.kind}`
      return null
    case "agent":
      if (f.kind !== "workspace" && f.kind !== "platform")
        return `a platform turn is paid by the workspace or the platform, not ${f.kind}`
      if (f.ownerId !== null) return "a platform turn names no person as payer"
      return null
    default:
      return `${actor._tag} names no payer`
  }
}

/**
 * Why a compute row's own payer is not acceptable, or null. A platform turn (agent) follows `fundedByProblem`. A runner
 * actor's compute rows come only from the hub for its Platform Runner (the controller measures the task sandbox; a
 * runner's Final carries no compute, decision 0107): the platform pays, no person, and the runner is the actor itself.
 */
export const computeFundedByProblem = (actor: Actor, f: FundedBy): string | null => {
  if (actor._tag !== "runner") return fundedByProblem(actor, f)
  if (f.kind !== "platform") return `a platform sandbox is paid by the platform, not ${f.kind}`
  if (f.ownerId !== null) return "a platform sandbox names no person as payer"
  if (f.runnerId !== actor.runnerId) return "a runner names only itself as the runner that ran the sandbox"
  return null
}

/**
 * The usage, funding and compute of a RecordRunEnded as decide records them: runner rows sanitized, a row's
 * unacceptable `fundedBy` override dropped, every row priced (`priceUsage`), the run's `fundedBy` kept only when
 * acceptable, compute rows kept only when the workspace or the platform pays them, each re-priced from its rate.
 */
export const recordedUsage = (
  actor: Actor,
  c: {
    readonly usage: ReadonlyArray<Usage>
    readonly fundedBy?: FundedBy | undefined
    readonly compute?: ReadonlyArray<ComputeUsage> | undefined
  },
): {
  usage: ReadonlyArray<Usage>
  fundedBy: FundedBy | undefined
  compute: ReadonlyArray<ComputeUsage> | undefined
  problems: ReadonlyArray<string>
} => {
  const problems: string[] = []
  const fundedBy =
    c.fundedBy === undefined
      ? undefined
      : (() => {
          const p = fundedByProblem(actor, c.fundedBy)
          if (p) problems.push(`fundedBy: ${p}`)
          return p ? undefined : c.fundedBy
        })()
  const usage = c.usage.map((u0) => {
    if (actor._tag === "runner" && u0.fundedBy !== undefined)
      problems.push("usage fundedBy: a runner sends no per-row payer (dropped)")
    let u = actor._tag === "runner" ? sanitizeRunnerUsage(u0) : u0
    if (u.fundedBy !== undefined) {
      const p = fundedByProblem(actor, u.fundedBy)
      if (p) {
        problems.push(`usage fundedBy: ${p}`)
        const { fundedBy: _f, ...rest } = u
        u = rest
      }
    }
    return priceUsage(u)
  })
  let compute: ComputeUsage[] | undefined
  if (c.compute !== undefined) {
    compute = []
    for (const row of c.compute) {
      const f = row.fundedBy ?? fundedBy
      if (!f || (f.kind !== "workspace" && f.kind !== "platform")) {
        problems.push("compute: only the workspace or the platform pays for compute")
        continue
      }
      if (row.fundedBy !== undefined) {
        const p = computeFundedByProblem(actor, row.fundedBy)
        if (p) {
          problems.push(`compute fundedBy: ${p}`)
          continue
        }
      }
      compute.push({ ...row, listMicroUsd: computeListMicroUsd(row.rateMicroUsdPerHour, row.runningSeconds) })
    }
  }
  return { usage, fundedBy, compute: compute && compute.length > 0 ? compute : undefined, problems }
}

/**
 * A turn's `askedBy` as decide records it: a person is kept as given (member or not; members sync asynchronously),
 * another agent's principal becomes the turn's own agent, absent is the agent herself. `replaced` lets the cloud log.
 */
export const turnAskedBy = (
  asked: Principal | undefined,
  agentId: Extract<Principal, { _tag: "agent" }>["agentId"],
): { askedBy: Principal; replaced: boolean } => {
  if (!asked) return { askedBy: { _tag: "agent", agentId }, replaced: false }
  if (asked._tag === "user" || asked.agentId === agentId) return { askedBy: asked, replaced: false }
  return { askedBy: { _tag: "agent", agentId }, replaced: true }
}
// #endregion decide
